import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { pipe, Option } from 'effect';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import emptyStateImage from '@/pages/company/BankFeedRoute/components/EmptyStates/image.svg';
import type { EmptyStateVariant } from '@/pages/company/BankFeedRoute/components/EmptyStates/computeEmptyVariant';
import { useBankFeedConnectionSettingsLink } from '@/pages/company/BankFeedRoute/useBankFeedConnectionSettingsLink';

type EmptyStateCopy = {
  titleId: string;
  defaultTitle: string;
  descriptionId: string;
  defaultDescription: string;
};

const COPY: Record<EmptyStateVariant, EmptyStateCopy> = {
  'no-connections': {
    titleId: 'company.bankFeed.empty.noConnections.title',
    defaultTitle: 'No accounts connected yet',
    descriptionId: 'company.bankFeed.empty.noConnections.description',
    defaultDescription:
      'You don’t have any transactions yet because you didn’t connect to any bank account. Please, go to the Settings to connect accounts or contact your admin',
  },
  'no-transactions': {
    titleId: 'company.bankFeed.empty.noTransactions.title',
    defaultTitle: 'No transactions yet',
    descriptionId: 'company.bankFeed.empty.noTransactions.description',
    defaultDescription:
      'You don’t have any transactions yet because you didn’t sync with your ERP and bank. Please go to the Settings to synchronize data or contact your admin',
  },
  connecting: {
    titleId: 'company.bankFeed.empty.connecting.title',
    defaultTitle: 'Syncing your bank feed',
    descriptionId: 'company.bankFeed.empty.connecting.description',
    defaultDescription:
      "We're connecting to your bank and syncing all transactions. This may take a few minutes — please wait while we finish.",
  },
  'no-filter-results': {
    titleId: 'empty.grid.noResults.title',
    defaultTitle: 'No results found',
    descriptionId: 'empty.grid.noResults.description',
    defaultDescription: 'No results match the selected filters.',
  },
};

// no-connections / no-transactions show the "Open settings" CTA to a user who may open the
// bank-feed connections settings.
const CTA_VARIANTS: readonly EmptyStateVariant[] = ['no-connections', 'no-transactions'];

type EmptyStateProps = {
  variant: EmptyStateVariant;
};

// All BankFeed empty-state screens share the same centered layout (visual + title +
// body). `connecting` shows a spinner in place of the illustration and has no CTA;
// no-filter-results is plain text (consumed via AG Grid's noRowsOverlay slot).
const EmptyState: React.FC<EmptyStateProps> = ({ variant }) => {
  const settingsLink = useBankFeedConnectionSettingsLink();
  const copy = COPY[variant];
  const cta = pipe(
    settingsLink,
    Option.filter(() => CTA_VARIANTS.includes(variant)),
  );

  return (
    <Box
      height="100%"
      width="100%"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minWidth={500}
      maxWidth={700}
      m="auto"
      p={1}
      textAlign="center"
    >
      {variant === 'connecting' ? (
        <CircularProgress />
      ) : (
        <Box maxWidth={300} minHeight={0} maxHeight={240}>
          <img src={emptyStateImage} alt="" height="100%" />
        </Box>
      )}
      <Typography sx={{ typography: { xl: 'h5', xs: 'h6' }, fontWeight: { xl: 400, xs: 400 } }} mt={2}>
        <FormattedMessage id={copy.titleId} defaultMessage={copy.defaultTitle} />
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={2} sx={{ typography: { xl: 'body1', xs: 'body2' } }}>
        <FormattedMessage id={copy.descriptionId} defaultMessage={copy.defaultDescription} />
      </Typography>
      {Option.match(cta, {
        onNone: () => null,
        onSome: (to) => (
          <Button color="primary" variant="contained" component={RouterLink} to={to} sx={{ borderRadius: '20px' }}>
            <FormattedMessage id="company.bankFeed.empty.openSettings" defaultMessage="Open settings" />
          </Button>
        ),
      })}
    </Box>
  );
};

export default EmptyState;
